import { movies } from "@/mocks/movies";
import { tvShows } from "@/mocks/tv";
import { varietyShows } from "@/mocks/variety";
import type { CategoryConfig, MediaItem, MediaType } from "@/types/media";

export const allMedia: MediaItem[] = [...movies, ...tvShows, ...varietyShows];

export const categories: CategoryConfig[] = [
  {
    key: "movie",
    name: "电影",
    path: "/category/movie",
    icon: "ri-film-line",
    intro: "院线大片与影史佳作汇聚，从科幻史诗到文艺小品，一次看个够。",
    description:
      "汇集全球热门电影资源，覆盖科幻、悬疑、动作、爱情、剧情等多种类型，支持按地区与年份筛选，快速找到你想看的那一部。",
  },
  {
    key: "tv",
    name: "电视剧",
    path: "/category/tv",
    icon: "ri-tv-2-line",
    intro: "国产、美剧、韩剧、日剧、港剧一网打尽，追剧党的高能补给站。",
    description:
      "精选海量热门电视剧，涵盖悬疑、古装、都市、爱情、犯罪等题材，剧集信息完整，助你轻松追剧不迷路。",
  },
  {
    key: "variety",
    name: "综艺",
    path: "/category/variety",
    icon: "ri-mic-line",
    intro: "真人秀、音乐竞演、美食旅行，热门综艺陪你下饭又解压。",
    description:
      "聚合当下最受欢迎的综艺节目，包括真人秀、音乐、喜剧、美食、选秀等类型，随时发现让人上头的快乐。",
  },
];

export function getCategoryConfig(type: string): CategoryConfig | undefined {
  return categories.find((item) => item.key === type);
}

export function getMediaByType(type: MediaType | string): MediaItem[] {
  return allMedia.filter((item) => item.type === type);
}

export function getMediaById(id: string): MediaItem | undefined {
  return allMedia.find((item) => item.id === id);
}

export function getLatestMedia(limit = 12): MediaItem[] {
  return [...allMedia]
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, limit);
}

export function getTopViewed(limit = 10): MediaItem[] {
  return [...allMedia].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getFeatured(limit = 4): MediaItem[] {
  const picks = ["movie-03", "movie-19", "tv-02", "movie-01"];
  const featured = allMedia.filter((item) => picks.includes(item.id));
  return featured.slice(0, limit);
}

export function getRelated(item: MediaItem, limit = 6): MediaItem[] {
  const scored = allMedia
    .filter((other) => other.id !== item.id)
    .map((other) => {
      let score = 0;
      if (other.type === item.type) score += 2;
      if (other.genre === item.genre) score += 3;
      const sharedActors = other.actors.filter((actor) => item.actors.includes(actor));
      score += sharedActors.length * 2;
      const sharedTags = other.tags.filter((tag) => item.tags.includes(tag));
      score += sharedTags.length;
      return { other, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.other.rating - a.other.rating);

  return scored.slice(0, limit).map((entry) => entry.other);
}

export interface MediaFilters {
  genres: string[];
  regions: string[];
  years: string[];
}

export function getFilterOptions(items: MediaItem[]) {
  const genres = Array.from(new Set(items.map((item) => item.genre)));
  const regions = Array.from(new Set(items.map((item) => item.region)));
  const years = Array.from(new Set(items.map((item) => String(item.year)))).sort(
    (a, b) => Number(b) - Number(a),
  );
  return { genres, regions, years };
}

export function applyFilters(items: MediaItem[], filters: MediaFilters): MediaItem[] {
  return items.filter((item) => {
    const genreOk = filters.genres.length === 0 || filters.genres.includes(item.genre);
    const regionOk = filters.regions.length === 0 || filters.regions.includes(item.region);
    const yearOk = filters.years.length === 0 || filters.years.includes(String(item.year));
    return genreOk && regionOk && yearOk;
  });
}

export type SortKey = "latest" | "hot" | "rating";

export const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "按上映时间" },
  { key: "hot", label: "按热门程度" },
  { key: "rating", label: "按评分" },
];

export function sortMedia(items: MediaItem[], sort: SortKey): MediaItem[] {
  const list = [...items];
  if (sort === "hot") return list.sort((a, b) => b.views - a.views);
  if (sort === "rating") return list.sort((a, b) => b.rating - a.rating);
  return list.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function searchMedia(keyword: string, items: MediaItem[] = allMedia): MediaItem[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return items.filter(
    (item) =>
      item.title.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query) ||
      item.genre.toLowerCase().includes(query) ||
      item.actors.some((actor) => actor.toLowerCase().includes(query)),
  );
}

export const PAGE_SIZE = 20;